import { useState } from "react";
import { CircleSlash } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/api";
import { DIARY_MAX_LENGTH } from "@/utils/trialFieldLimits";
import { apiErrorMessage } from "@/utils/apiError";
import CharCount from "@/components/CharCount";

export function CancelTaskDialog({ trialId, task, onTaskCancel, size, className }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);

  // Reset stale input/error from a previous attempt whenever the dialog is (re)opened.
  const handleOpenChange = (next) => {
    if (next) {
      setReason("");
      setError(null);
    }
    setOpen(next);
  };

  const handleTaskCancel = async (event) => {
    // Prevent the default auto-close so an empty reason or a failed request keeps
    // the dialog open (and the typed reason) instead of silently discarding it.
    event.preventDefault();

    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setError("Please provide a reason before cancelling this task.");
      return;
    }

    try {
      await api.patch(`/trials/${trialId}/diaries/${task.id}`, {
        cancelled: true,
        cancelled_reason: trimmedReason,
      });
      setOpen(false);
      onTaskCancel();
    } catch (err) {
      console.error("Error cancelling task:", err);
      toast.error(apiErrorMessage(err, "Failed to cancel task. Please try again."));
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        <Button variant="destructive" size={size} className={className} aria-label="Cancel task" title="Cancel task">
          <CircleSlash className="h-4 w-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel this task?</AlertDialogTitle>
          <AlertDialogDescription>
            This will mark the task as cancelled. Please provide a reason -
            you'll be able to reopen the task later if needed.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="grid gap-2">
          <Label htmlFor="cancel-reason">Reason</Label>
          <Input
            id="cancel-reason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError(null);
            }}
            placeholder="Why is this task being cancelled?"
            maxLength={DIARY_MAX_LENGTH.cancelled_reason}
            autoFocus
          />
          <CharCount value={reason} max={DIARY_MAX_LENGTH.cancelled_reason} />
          {error && (
            <span className="text-sm text-destructive">{error}</span>
          )}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleTaskCancel}
            disabled={!reason.trim()}
          >
            Continue
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
